import { Container, Modal, Tabs } from '@/components';
import { useAttachUserCompany } from '@/modules/Companies/queries/attach-user-company';
import { useCreateUser } from '@/modules/Users/queries/create-user';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddCsvTabModal } from '../AddCsvTab';
import { AssociateUserTab } from '../AssociateUserTab/AssociateUserTab';
import { UserModal } from '../UserModal';

type CreateCompanyUserModalProps = {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
};

export const CreateCompanyUserModal = ({
  isOpen,
  onClose,
  companyId,
}: CreateCompanyUserModalProps) => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);

  const { mutate: attachUserCompany, isLoading: isLoadingAttachUserCompany } =
    useAttachUserCompany();

  const onSubmitUserAssociation = useCallback(
    (userIds: number[]) => {
      attachUserCompany(
        {
          companyId,
          userIds,
        },
        {
          onSuccess: () => {
            onClose();
          },
        },
      );
    },
    [attachUserCompany, companyId, onClose],
  );

  const { mutate: createUser, isLoading: IsLoadingCreateUser } = useCreateUser(
    ({ data: { userId } }) => {
      onSubmitUserAssociation([userId]);
    },
  );

  const modalTitle = selectedTab !== 2 ? 'create_users' : 'add_users';

  return (
    <Modal
      className="h-fit w-128"
      isOpen={isOpen}
      title={t(modalTitle)}
      handleClose={onClose}
    >
      <div className="mt-6 flex flex-col">
        <Container>
          <Tabs
            activeTab={selectedTab}
            tabs={[
              { label: t('create_new') },
              {
                label: t('add_through_csv'),
              },
              {
                label: t('add_existing_users'),
              },
            ]}
            onTabChange={setSelectedTab}
          />
        </Container>
        {selectedTab === 0 && (
          <UserModal
            isLoading={IsLoadingCreateUser || isLoadingAttachUserCompany}
            handleOnSubmit={(user) =>
              createUser({
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                status: user.status,
                access: user.access,
              })
            }
            handleClose={onClose}
            type="create"
          />
        )}
        {selectedTab === 1 && (
          <AddCsvTabModal companyId={companyId} handleClose={onClose} />
        )}
        {selectedTab === 2 && (
          <AssociateUserTab
            onClose={onClose}
            onSubmit={(items) =>
              onSubmitUserAssociation(items.map((item) => item.id))
            }
            isSubmitting={isLoadingAttachUserCompany}
          />
        )}
      </div>
    </Modal>
  );
};
